import GridSpinner from 'components/UI/GridSpinner'
import { Button, Grid, Icon, Tooltip } from 'gu-ui-kit'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import PageHeader from 'components/PageHeader'
import { usePromisedPayment } from 'modules/Finance/PromisedPayment/hooks/usePromisedPayment'
import Table from 'modules/Finance/PromisedPayment/subcomponents/Table'
import EmptyResults from 'components/EmptyResults'
import { Link } from 'react-router-dom'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

const PromisedPayment = () => {
  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()
  const breakpoint = useBreakpoint()
  const [ready, setReady] = useState(false)
  const [preReady, setPreReady] = useState(false)
  const [hasPromisedPayment, setHasPromisedPayment] = useState(false)
  const [hasTakenPromisedPayment, setHasTakenPromisedPayment] = useState(false)

  const {
    settings = {},
    promisedPayment = {},
    takenPromisedPayment = {},
  } = useSelector((state) => state['finance/promised-payment'])

  const { i18n } = useSelector(({ app }) => app)

  const {
    getSettings,
    getTakenPromisedPayment,
    resetPromisedPayment,
    getPromisedPayment,
    dropPromisedPayment,
  } = usePromisedPayment()

  useEffect(() => {
    getSettings()
  }, [i18n])

  useEffect(() => {
    if (!settings.loaded || settings.loading || !settings.available || ready) {
      return
    }
    getTakenPromisedPayment()
    getPromisedPayment()
  }, [settings])

  useEffect(() => {
    return () => {
      resetPromisedPayment()
    }
  }, [])

  useEffect(() => {
    if (ready || preReady || !settings.loaded) {
      return
    }

    if (!settings.available) {
      setReady(true)
      return
    }

    if (!promisedPayment.loaded || !takenPromisedPayment.loaded) {
      return
    }

    setHasPromisedPayment(promisedPayment.loaded && promisedPayment?.data?.length > 0)
    setHasTakenPromisedPayment(
      takenPromisedPayment.loaded && takenPromisedPayment?.data?.length > 0,
    )
    dropPromisedPayment()
    setPreReady(true)
  }, [settings, promisedPayment, takenPromisedPayment, ready])

  useEffect(() => {
    if (preReady && promisedPayment?.data?.length === 0) {
      setReady(true)
    }
  }, [promisedPayment])

  if (!ready) {
    return <GridSpinner />
  }

  if (!settings.available) {
    return (
      <div className={'layout-tile margin-bottom-16'}>
        <EmptyResults
          header={t('promised_payment::not_available.heading')}
          text={t('promised_payment::not_available.summary')}
          actionButton={
            <Button
              size={'l'}
              variant={'primary'}
              component={'a'}
              target={'_blank'}
              grey
              href={pathGen(ROUTES.PROMISED_PAYMENT_TERMS)}
              startIcon={<Icon name={'document'} />}
            >
              {t('common::buttons.terms')}
            </Button>
          }
          icon={
            <Icon
              name={'time-money'}
              color={'Interface-Onsurface'}
              width={64}
            />
          }
        />
      </div>
    )
  }

  if (settings.available && !hasPromisedPayment) {
    return (
      <div className={'layout-tile margin-bottom-16'}>
        <EmptyResults
          header={t('promised_payment::totally_empty_data.heading')}
          text={t('promised_payment::totally_empty_data.summary')}
          actionButton={
            <Grid
              justifyContent={'center'}
              gap={16}
            >
              <Button
                size={'l'}
                variant={'primary'}
                component={'a'}
                target={'_blank'}
                grey
                href={pathGen(ROUTES.PROMISED_PAYMENT_TERMS)}
                startIcon={<Icon name={'document'} />}
              >
                {t('common::buttons.terms')}
              </Button>
              <Button
                size={'l'}
                variant={'primary'}
                component={Link}
                to={pathGen(ROUTES.FINANCE_PROMISED_PAYMENT_REQUEST)}
                startIcon={<Icon name={'plus'} />}
              >
                {t('common::buttons.create_request')}
              </Button>
            </Grid>
          }
          icon={
            <Icon
              name={'time-money'}
              color={'Interface-Onsurface'}
              width={64}
            />
          }
        />
      </div>
    )
  }

  return (
    <>
      <div className={'layout-tile margin-bottom-16'}>
        <Grid
          alignItems={'center'}
          justifyContent={'start'}
          gap={8}
          className={'margin-bottom-24'}
        >
          <PageHeader>{t('promised_payment::heading')}</PageHeader>
          <Button
            component={'a'}
            target={'_blank'}
            href={pathGen(ROUTES.PROMISED_PAYMENT_TERMS)}
            variant={'ghost'}
            size={'m'}
            noLabel
            startIcon={
              <Icon
                name={'information'}
                color={'Interface-Seventh'}
                size={'m'}
              />
            }
          ></Button>
        </Grid>
        {hasTakenPromisedPayment ? (
          <Tooltip title={t('promised_payment::expired_promised_payment.tooltip')}>
            <Button
              fullWidth={!breakpoint.sm}
              variant={'primary'}
              size={'l'}
              startIcon={<Icon name={'plus'} />}
              disabled
            >
              {t('common::buttons.create_request')}
            </Button>
          </Tooltip>
        ) : (
          <Button
            fullWidth={!breakpoint.sm}
            variant={'primary'}
            size={'l'}
            startIcon={<Icon name={'plus'} />}
            component={Link}
            to={pathGen(ROUTES.FINANCE_PROMISED_PAYMENT_REQUEST)}
          >
            {t('common::buttons.create_request')}
          </Button>
        )}
      </div>
      <div className={'layout-tile'}>
        <Table />
      </div>
    </>
  )
}

export default PromisedPayment
